import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useWatch } from "react-hook-form";

import { useGetTimezones } from "api/queries/common/useGetTimezones";
import { useGetServiceDetailsConfig } from "api/queries/location/useGetServiceDetailsConfig";
import Card from "components/v2/Card/Card";
import testIds from "constants/testIds";
import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import type { ConsigneeValue } from "@RequestFormV2/defaults/consigneeDefaults";
import type { NotifyPartyValue } from "@RequestFormV2/defaults/notifyPartyDefaults";
import type { Service } from "api/models/draftRequest.model";
import { ReviewClearanceFields } from "@RequestFormV2/components/ReviewPage/sections/reviewClearance";
import { getFinalDeliveryStop } from "@RequestFormV2/components/ReviewPage/sections/ReviewSchedule/partyReviewSyncKind";
import { hasStopData } from "@RequestFormV2/components/ReviewPage/sections/ReviewSchedule/reviewScheduleFormatters";
import {
  ConsigneeNotifyRow,
  StopRow,
} from "@RequestFormV2/components/ReviewPage/sections/ReviewSchedule/ReviewScheduleStopRow";
import ReviewScheduleStopsTable from "@RequestFormV2/components/ReviewPage/sections/ReviewSchedule/ReviewScheduleStopsTable";
import { useIsQuickRequest } from "@RequestFormV2/context/requestFormMode";
import { getHasMultipleCountries } from "@RequestFormV2/validation/getHasMultipleCountries";

type TimezoneOption = { value: string; label: string };

export default function ReviewSchedule() {
  const intl = useIntl();
  const isQuickRequest = useIsQuickRequest();
  const stops = useWatch({ name: "schedule.stops" }) as ScheduleStopValue[] | undefined;
  const service = useWatch({ name: "service" }) as Service | undefined;
  const consignee = useWatch({ name: "schedule.consignee" }) as ConsigneeValue | undefined;
  const notifyParty = useWatch({ name: "schedule.notify_party" }) as NotifyPartyValue | undefined;
  const multistopInstructions = useWatch({ name: "schedule.multistop_instructions" }) as string | undefined;
  const location = useWatch({ name: "organizational.location" }) as string | undefined;

  const { data: timezonesData } = useGetTimezones({}, {
    select: (data: any) => data.map((tz: any) => ({ value: tz.value, label: tz.name })),
  });
  const timezones = (timezonesData ?? []) as TimezoneOption[];
  const { data: serviceDetailsConfig } = useGetServiceDetailsConfig(location ?? "", {
    enabled: !!location,
  });

  const getTimezoneLabel = useCallback(
    (value?: string | null) => {
      if (!value) return "";
      return timezones.find((tz) => tz.value === value)?.label ?? value;
    },
    [timezones],
  );

  const validStops = (stops ?? []).filter(hasStopData);
  const isMultiStop = (stops?.length ?? 0) > 2;
  const finalDeliveryStop = getFinalDeliveryStop(validStops);

  const hasMultipleCountries = useMemo(() => getHasMultipleCountries(stops), [stops]);
  const showClearance =
    hasMultipleCountries && (!isQuickRequest || !!serviceDetailsConfig?.service_options?.ask_clearance);

  return (
    <Card borderRadius="xl" shadow="xs" data-testid={testIds.REQUEST_FORM_REVIEW_SCHEDULE}>
      <Card.Header title={intl.formatMessage({ id: "request-form.schedule", defaultMessage: "Schedule" })} showBorder />
      <Card.Body>
        <div className="rfv2-review__section-content rfv2-review__section-content--schedule">
          {validStops.length === 0 ? (
            <span className="rfv2-review__empty">
              {intl.formatMessage({
                id: "request-form.review.schedule.empty",
                defaultMessage: "No schedule stops added",
              })}
            </span>
          ) : (
            <>
              {isMultiStop ? (
                <>
                  <ReviewScheduleStopsTable stops={validStops} getTimezoneLabel={getTimezoneLabel} />
                  {multistopInstructions && (
                    <div className="rfv2-review__field">
                      <span className="rfv2-review__field-label">
                        {intl.formatMessage({
                          id: "request-form.schedule.multistopInstructions",
                          defaultMessage: "Multistop instructions",
                        })}
                      </span>
                      <span className="rfv2-review__field-value">{multistopInstructions}</span>
                    </div>
                  )}
                </>
              ) : (
                validStops.map((stop) => (
                  <StopRow key={stop.stop_key} stop={stop} getTimezoneLabel={getTimezoneLabel} />
                ))
              )}
              {showClearance && <ReviewClearanceFields service={service} intl={intl} />}
              <ConsigneeNotifyRow
                consignee={consignee}
                notifyParty={notifyParty}
                deliveryStop={finalDeliveryStop}
              />
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
